import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from '../../../../stores/useTheme';
import OverviewWidget from '../../Widgets/OverviewWidget/OverviewWidget';
import StatsWidget from '../../Widgets/StatsWidget/StatsWidget';
import PlayersWidget from '../../Widgets/PlayersWidget/PlayersWidget';
import LineupWidget from '../../Widgets/LineupWidget/LineupWidget';
import H2HWidget from '../../Widgets/H2HWidget/H2HWidget';
import { getIsMobileDevice, getKeyByValue, getUrlParam } from '@utils/functions';
import { locale_en_main_menu_names } from "../../../../translation/defaultTranslations";
import { useLanguage } from '@utils/hooks/useLanguage';
import { T, UrlParams } from '@utils/enums';
import { LeftArrowInTabsMenu } from '@components/StatsConsole/Layout/parts/MenuArrows/LeftArrowInTabsMenu';
import { RightArrowInTabsMenu } from '@components/StatsConsole/Layout/parts/MenuArrows/RightArrowInTabsMenu';
import scrollPartiallyVisibleMenuItemIntoView from './hooks/scrollPartiallyVisibleMenuItemIntoView';
import MainMenuButton from './MainMenuButton';
import StandardStatsConsole from './parts/StandardStatsConsole';
import { useVersion } from '@utils/hooks/useVersion';
import { isDataAvailable } from '@utils/functions';
import { useUrlParams } from '@utils/hooks/useUrlParams';
import { IState, StateContext } from '../../../../state/stateContext';
import WidgetWrapper from '@components/StatsConsole/Widgets/WidgetWrapper/WidgetWrapper';
import { useOverviewDataProcessing } from '@components/StatsConsole/Widgets/OverviewWidget/hooks/useOverviewDataProcessing';

export interface MatchClockData {
    minutes: number;
    seconds: number;
    isRunning: boolean;
    formattedTime: string | null;
    currentPeriod: number;
    time_start: string;
    isExtraTimeFirstHalf: boolean;
    isExtraTimeSecondHalf: boolean;
    isHalfTimeOfExtraTime: boolean;
    isFullTime: boolean;
    isHalfTime: boolean;
    isPenalties: boolean;
    isPreMatch: boolean;
    isEndOfSecondHalf: boolean;
    isEndOfExtraTime: boolean;
    minute: number;
}

const menuItems = [
    T.TAB_STATS,
    T.TAB_OVERVIEW,
    T.TAB_PLAYERS,
    T.TAB_LINEUP,
    T.TAB_H2H,
] as const;

type TabName = typeof menuItems[number];

const isTabName = (value: string): value is TabName => {
    return menuItems.includes(value as TabName);
};


interface StandardAndExpandedStatsConsoleProps {
    isExpanded: boolean;
    eventId: string | null;
    overviewData: any;
    homeScore: number;
    awayScore: number;
    statsTabData: any;
    playersTabData: any;
    h2hData: any;
    lineupData: any;
    title: string;
    isMVP?: boolean;
    fixedHeight?: string;
    matchTime: MatchClockData  | undefined;
    homeTeamName?: string | null;
    awayTeamName?: string | null;
}

export const StandardAndExpandedStatsConsole = ({ 
    isExpanded, 
    eventId, 
    overviewData, 
    homeScore, 
    awayScore, 
    statsTabData, 
    playersTabData, 
    title, 
    h2hData,
    lineupData,
    isMVP = true,
    fixedHeight = '341px',
    matchTime,
    homeTeamName,
    awayTeamName,
}: StandardAndExpandedStatsConsoleProps) => {
    const [isExpandedWidget, setIsExpandedWidget] = useState(isExpanded);
    const [height, setHeight] = useState<string>('41px');
    const { theme, toggleTheme, version } = useTheme();
    const language = useLanguage();
    const { isIOS } = useUrlParams();
    const defSCTab = getUrlParam(UrlParams.DEFAULT_STATS_CONSOLE_TAB);
    // Used to set active tab when it's possible to verify if specific tab is enabled.
    const [isActiveMainMenuButtonName, setActiveMainMenuButtonName] = useState(() => {
        if (defSCTab) {
            // Special handling for H2H - URL uses "H2H" but translation is "H2H & Table"
            if (defSCTab === 'H2H') {
                return T.TAB_H2H;
            }
            const key = getKeyByValue(locale_en_main_menu_names, defSCTab);
            return key || T.TAB_STATS;
        }
        return T.TAB_STATS;
    });
    const [isLeftArrowDisabled, setIsLeftArrowDisabled] = useState(true);
    const [isRightArrowDisabled, setIsRightArrowDisabled] = useState(true);
    const [isMenuItemsScrollable, setIsMenuItemsScrollable] = useState(false);
    const [isMenuArrowsVisible, setIsMenuArrowsVisible] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const menuItemsContainerRef = useRef<HTMLDivElement>(null);
    const menuItemsRef = useRef<HTMLDivElement>(null);
    const [isMenuHovered, setIsMenuHovered] = useState(false);
    const { isV1 } = useVersion();
    const ctx = useContext(StateContext);
    const { setStContext } = ctx || {};

    const {
        overviewDataSorted,
        isHalfTimeFirstEvent,
        isFullTimeFirstEvent,
        isExtraTimeFirstEvent,
        isPenaltiesFirstEvent,
    } = useOverviewDataProcessing({ overviewData, eventId });

    const isStatsWidgetPending = !statsTabData || !overviewData;
    const isOverviewWidgetPending = !overviewData?.data;
    const isPlayersWidgetPending = !homeTeamName || !awayTeamName || !playersTabData?.data;
    const isLineupWidgetPending = !lineupData?.data;

    const { goalsLineWidthHome, goalsLineWidthAway } = useMemo(() => { 
        if (homeScore === null || awayScore === null) {
            return {
                goalsLineWidthHome: 50,
                goalsLineWidthAway: 50
            };
        }
        const totalScore = homeScore + awayScore;
        if (totalScore > 0) {
            return {
                goalsLineWidthHome: (homeScore / totalScore) * 100,
                goalsLineWidthAway: (awayScore / totalScore) * 100
            };
        } else {
            return {
                goalsLineWidthHome: 50,
                goalsLineWidthAway: 50
            };
        }
    }, [homeScore, awayScore]);

    const checkIsMainMenuButtonDisabled = useCallback(
       (menuItem: string): boolean => {    
        switch (menuItem) {
            case T.TAB_STATS:
                return !isDataAvailable(statsTabData) || isStatsWidgetPending;
            case T.TAB_OVERVIEW:
                return !isDataAvailable(overviewData) || isOverviewWidgetPending;
            case T.TAB_PLAYERS:
                return !isDataAvailable(playersTabData) || isPlayersWidgetPending;
            case T.TAB_LINEUP:
                return !isDataAvailable(lineupData) || isLineupWidgetPending;
            case T.TAB_H2H:
                return !isDataAvailable(h2hData);
            default:
                return false;
        }
        },
        [statsTabData, isStatsWidgetPending, overviewData, isOverviewWidgetPending, playersTabData, isPlayersWidgetPending, lineupData, isLineupWidgetPending, h2hData]
    );

    const calcExpandedHeight = (height: string) => {
        let decodedHeight = height;
        try {
            decodedHeight = decodeURIComponent(height);
        } catch (e) {
            decodedHeight = height;
        }
        
        // Extract number and unit from string (including 'p' as percent substitute)
        const match = decodedHeight.match(/^(\d+(?:\.\d+)?)(px|vh|%|p|em|rem)?$/);
        
        if (!match) {
            return '301px'; // 341px - 40px as fallback
        }
        
        const [, numberStr, unit = 'px'] = match;
        const number = parseFloat(numberStr);
        
        if (isNaN(number)) {
            return '301px';
        }
        
        
        switch (unit) {
            case 'px':
                const newPxValue = Math.max(number - 40, 0);
                return `${newPxValue}px`;
            case 'vh':
                const vhResult = `calc(${number}vh - 40px)`;
                return vhResult;
            case '%':
            case 'p':
                const vhValue = number; // 100% = 100vh in iframe
                const percentResult = `calc(${vhValue}vh - 40px)`;
                return percentResult;
            case 'em':
            case 'rem':
                const emResult = `calc(${number}${unit} - 40px)`;
                return emResult;
            default:
                const defaultResult = `${Math.max(number - 40, 0)}px`;
                return defaultResult;
        }
    }

    const contentHeight = useMemo(() => {
        if (!isExpandedWidget) return 'auto';
        const calculated = calcExpandedHeight(height);
        return calculated;
    }, [height, isExpandedWidget]);

    
    const handleTabChange = (newTab: string) => {
        const scrollContainer = document.getElementById('custom-scrollbar') as HTMLDivElement;
        if (scrollContainer) {
            scrollContainer.scrollTop = 0;
        }
        
        if (isTabName(newTab)) {
            setActiveMainMenuButtonName(newTab);
        }
    }
    
    const expandedContent = () => {
        switch (isActiveMainMenuButtonName) {
            case T.TAB_STATS:
                return (
                    <WidgetWrapper eventId={eventId} statsData={overviewData} statsTabData={statsTabData} overviewData={overviewData} isMVP={isMVP} matchTime={matchTime} childWidgetName={T.TAB_STATS} homeTeam={homeTeamName} awayTeam={awayTeamName} isStatsWidgetPending={isStatsWidgetPending}>
                        <StatsWidget eventId={eventId} statsData={overviewData} overviewData={overviewData} statsTabData={statsTabData} isMVP={isMVP} matchTime={matchTime} />
                    </WidgetWrapper>
                )
            case T.TAB_OVERVIEW:
                return (
                    <WidgetWrapper eventId={eventId} statsData={overviewData} statsTabData={statsTabData} overviewData={overviewData} overviewDataSorted={overviewDataSorted} isMVP={isMVP} matchTime={matchTime} childWidgetName={T.TAB_OVERVIEW} homeTeam={homeTeamName} awayTeam={awayTeamName} isOverviewWidgetPending={isOverviewWidgetPending}>
                        <OverviewWidget 
                            overviewDataSorted={overviewDataSorted} 
                            isHalfTimeFirstEvent={isHalfTimeFirstEvent} 
                            isFullTimeFirstEvent={isFullTimeFirstEvent} 
                            isExtraTimeFirstEvent={isExtraTimeFirstEvent} 
                            isPenaltiesFirstEvent={isPenaltiesFirstEvent} 
                            isMVP={isMVP} 
                            matchTime={matchTime} 
                        />
                    </WidgetWrapper>
                )
            case T.TAB_PLAYERS:
                return (
                    <WidgetWrapper eventId={eventId} statsData={overviewData} statsTabData={statsTabData} overviewData={overviewData} isMVP={isMVP} matchTime={matchTime} childWidgetName={T.TAB_PLAYERS} homeTeam={homeTeamName} awayTeam={awayTeamName} homeFormation={lineupData?.data?.homeFormation} awayFormation={lineupData?.data?.awayFormation} playersTabData={playersTabData} isPlayersWidgetPending={isPlayersWidgetPending}>
                        <PlayersWidget eventId={eventId} statsData={overviewData} playersTabData={playersTabData} />
                    </WidgetWrapper>
                )
            case T.TAB_LINEUP:
                return (
                    <WidgetWrapper eventId={eventId} statsData={overviewData} statsTabData={statsTabData} overviewData={overviewData} isMVP={isMVP} matchTime={matchTime} childWidgetName={T.TAB_LINEUP} homeTeam={homeTeamName} awayTeam={awayTeamName} homeFormation={lineupData?.data?.homeFormation} awayFormation={lineupData?.data?.awayFormation} lineupData={lineupData} isLineupWidgetPending={isLineupWidgetPending}>
                        <LineupWidget eventId={eventId} lineupData={lineupData} isMVP={isMVP} homeTeamName={homeTeamName} awayTeamName={awayTeamName} />
                    </WidgetWrapper>
                )
            case T.TAB_H2H:
                return (
                    <H2HWidget eventId={eventId} h2hData={h2hData}/>
                )
            default:
                return (
                    <WidgetWrapper eventId={eventId} statsData={overviewData} statsTabData={statsTabData} overviewData={overviewData} isMVP={isMVP} matchTime={matchTime} childWidgetName={T.TAB_STATS} homeTeam={homeTeamName} awayTeam={awayTeamName } isStatsWidgetPending={isStatsWidgetPending}>
                        <StatsWidget eventId={eventId} statsData={overviewData} overviewData={overviewData} statsTabData={statsTabData} isMVP={isMVP} matchTime={matchTime} />
                    </WidgetWrapper>
                )
        }
    }
    
    const onClickLeftArrow = () => {
        const menuItemsContainer = document.querySelector('.menuItems-disable-scrollbar');
        if (menuItemsContainer) {
            const currentScroll = menuItemsContainer.scrollLeft;
            const scrollAmount = Math.min(40, currentScroll);
            menuItemsContainer.scrollLeft -= scrollAmount;
        }
    }

    const onClickRightArrow = () => {
        const menuItemsContainer = document.querySelector('.menuItems-disable-scrollbar');
        if (menuItemsContainer) {
            const maxScroll = menuItemsContainer.scrollWidth - menuItemsContainer.clientWidth;
            const currentScroll = menuItemsContainer.scrollLeft;
            const remainingScroll = maxScroll - currentScroll;
            const scrollAmount = Math.min(40, remainingScroll);
            menuItemsContainer.scrollLeft += scrollAmount;
        }
    }

    const handleCloseTooltip = () => {
        setStContext((prev: IState) => ({
            ...prev,
            activeTooltipPlayerId: null
        }));
    }

    useEffect(() => {
        const message = {
            type: 'suzu_stats_console_iframe',
            height: height ? height : fixedHeight,
        };
        window.parent.postMessage(message, '*');
    }, [height]);

    useEffect(() => {
        if (isExpandedWidget) {
            if (fixedHeight) {
                setHeight(fixedHeight); 
            } else {
                setHeight('341px');
            }
        } else {
            setHeight('41px');
        }
    }, [isExpandedWidget, fixedHeight])


    useEffect(() => {
        const isMobileDevice = getIsMobileDevice();
        setIsMobile(isMobileDevice);
        if (isMobileDevice) {
            setIsMenuArrowsVisible(false); 
        } else {
            if (isMenuItemsScrollable) {
                setIsMenuArrowsVisible(true);
            } else {
                setIsMenuArrowsVisible(false);
            }
        }
    }, [isMenuItemsScrollable])

    useEffect(() => {
        const menuItemsContainer = document.querySelector('.menuItems-disable-scrollbar');
        if (menuItemsContainer) {
            const handleScroll = () => {
                const currentScroll = menuItemsContainer.scrollLeft;
                const maxScroll = menuItemsContainer.scrollWidth - menuItemsContainer.clientWidth;
                setIsLeftArrowDisabled(currentScroll === 0);
                setIsRightArrowDisabled(Math.abs(currentScroll - maxScroll) < 1);
            };
            
            menuItemsContainer.addEventListener('scroll', handleScroll);
            
            handleScroll();
            
            return () => {
                menuItemsContainer.removeEventListener('scroll', handleScroll);
            };
        }
    }, [isExpandedWidget]);

    useEffect(() => {
        const checkScrollability = () => {
            if (!isExpandedWidget) {
                setIsMenuItemsScrollable(false);
                return;
            }
            
            if (menuItemsContainerRef.current && menuItemsRef.current) {
                const containerWidth = menuItemsContainerRef.current.clientWidth;
                const itemsWidth = menuItemsRef.current.scrollWidth;
                if (containerWidth > 0 && itemsWidth > 0) {
                    setIsMenuItemsScrollable(itemsWidth > containerWidth-20);
                } else {
                    setTimeout(checkScrollability, 50);
                }
            } else {
                setIsMenuItemsScrollable(false);
            }
        };
    
        const timeoutId = setTimeout(checkScrollability, 100);
        let resizeObserver: ResizeObserver | null = null;
        if (menuItemsContainerRef.current) {
            resizeObserver = new ResizeObserver(() => {
                setTimeout(checkScrollability, 10);
            });
            resizeObserver.observe(menuItemsContainerRef.current);
        }
        
        window.addEventListener('resize', checkScrollability);
    
        return () => {
            clearTimeout(timeoutId);
            window.removeEventListener('resize', checkScrollability);
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
        };
    }, [language, isExpandedWidget]);

    useEffect(() => {
        if (isActiveMainMenuButtonName) {
            setTimeout(() => {
                scrollPartiallyVisibleMenuItemIntoView(isActiveMainMenuButtonName);
            }, 100);
        }
    }, [isExpandedWidget]);

    return (
        <div 
            data-testid="stats-console-standard-and-expanded-container"
            className={`select-none flex  overflow-hidden w-[100%] h-[${height}px]
            ${isV1 ? 'bg-monoY dark:bg-base110' : 'dark:bg-[#222222] bg-monoY'}
            ${isIOS ? 'rounded-[4px]' : ''}`}>
            <div className={`flex text-[14px] gap-[3px]
                ${isExpandedWidget ? 'max-w-full max-h-full min-w-full min-h-[60px] w-full h-full px-0 py-0 overflow-hidden' : 
                                     'max-w-fullp max-h-full min-w-full min-h-[41px] w-full h-[41px]'}
            `}>
                {isExpandedWidget ?
                    (
                        <div 
                            ref={menuItemsContainerRef} 
                            className={`w-[100%] overflow-hidden flex flex-col max-h-[${height}]`}> 
                            <div className={`flex flex-row w-full overflow-hidden  pt-[8px] px-[8px] 
                                             flex-shrink-0
                                            pl-[0px]
                                            ${isV1 ? 'h-[40px] max-h-[40px] border-b-[1px] border-base080 dark:border-base0 bg-monoY dark:bg-base110  ' 
                                                : 'h-[50px] max-h-[50px] dark:bg-[#222222] bg-monoY '}`}
                                onMouseEnter={() => setIsMenuHovered(true)}
                                onMouseLeave={() => setIsMenuHovered(false)}
                                onClick={handleCloseTooltip}
                            >
                                {isMenuArrowsVisible && 
                                    <LeftArrowInTabsMenu 
                                        color={theme === T.LIGHT ? (isLeftArrowDisabled ? '#AAAAAA' : '#555555') : (isLeftArrowDisabled ? '#8F8F8F' : '#FFFFFF')} 
                                        onClick={onClickLeftArrow}
                                        isVisibleOnHover={isMenuHovered}
                                        isArrowHidden={isLeftArrowDisabled}
                                        additionalClassName={`${isV1 ? 'relative left-[15px] top-[0px] z-10' : 'relative left-[8px] top-[-7px] z-10' }`}
                                        svgAdditionalClassName={` w-[16px] ${isV1 ? 'bg-monoY dark:bg-base110' : 'dark:bg-[#222222] bg-monoY'}`}
                                    />
                                }
                                <div ref={menuItemsRef} 
                                    data-testid="stats-console-expanded-tabs"
                                    className={`flex justify-start items-center 
                                                overflow-x-scroll overflow-y-hidden menuItems-disable-scrollbar w-full`}
                                >
                                    {menuItems.map((item, idx) => {
                                            return <MainMenuButton 
                                                key={idx} 
                                                titleKey={item} 
                                                onClick={handleTabChange} 
                                                isActive={isActiveMainMenuButtonName} 
                                                isDisabled={checkIsMainMenuButtonDisabled(item)}
                                                elementOrder={idx + 1}
                                                />
                                    })}
                                </div>
                                {isMenuArrowsVisible && 
                                    <RightArrowInTabsMenu 
                                        color={theme === T.LIGHT ? (isRightArrowDisabled ? '#AAAAAA' : '#555555') : (isRightArrowDisabled ? '#8F8F8F' : '#FFFFFF')} 
                                        onClick={onClickRightArrow}
                                        isVisibleOnHover={isMenuHovered}
                                        isArrowHidden={isRightArrowDisabled}
                                        additionalClassName={`${isV1 ? 'relative right-[15px] top-[0px] z-10' : 'relative right-[4px] top-[-7px] z-10' }`}
                                        svgAdditionalClassName={`${isV1 ? 'bg-monoY dark:bg-base110 w-[16px]' : 'dark:bg-[#222222] bg-monoY'}`}
                                    />
                                }
                            </div>
                            <div 
                                className="flex flex-row justify-center w-full flex-1 overflow-y-auto"
                                style={{
                                    maxHeight: contentHeight,
                                    minHeight: contentHeight
                                }}
                            >
                                    {expandedContent()}    
                            </div>
                        </div>
                    )
                    :
                    (
                       <StandardStatsConsole 
                        title={title} 
                        homeScore={homeScore} 
                        awayScore={awayScore} 
                        goalsLineWidthHome={goalsLineWidthHome} 
                        goalsLineWidthAway={goalsLineWidthAway} 
                        />
                    )}
            </div>
        </div>
    )
}

